import { useState, useEffect } from 'react';
import { Modal, Col, Row, Form, Input, message, Radio, Select, Spin, Button } from 'antd';
import { format } from 'rut.js';
import { createCompany, getCommunes, getCompany, getTributaryCompany, updateCompany } from '../../../services/general';
import { useTranslation } from 'react-i18next';
import { DocumentCard, PhoneInput } from 'cmt-ui-kit';
import { getCookie } from './../../../services/cookies';
import { handlers } from './../../../helpers/helpers';
const countrySettings = require('../countrySettings.json');

const { Option } = Select;

const CreateCompanyModal = ({
  companyModal,
  setCompanyModal,
  addCompany,
  reservationForm,
  formName,
  createTitle,
  editTitle,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState({ submit: false, fetch: false });
  const countryCode = getCookie('country_code')
  const [documentType, setDocumentType] = useState(countrySettings.documentType[countryCode]);
  const [communes, setCommunes] = useState();
  const [disable, setDisable] = useState(true);
  const { t } = useTranslation();

  const fetchCommunes = async () => {
    try {
      const response = await getCommunes({ country_code: countryCode });
      setCommunes(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al obtener las comunas',
        style: {
          marginTop: '30vh',
        },
      });
    }
  };

  const disableCompanyDataValidation = (data) => {
    const { documentTypes, activities } = countrySettings.editableCompanyData;
    const companyDocumentType = data?.documentType || data?.document_type;
    const normalizedActivity = (data?.activity || '').trim().toLowerCase();
    const isEditable = documentTypes[countryCode].includes(companyDocumentType)
      || activities[countryCode].includes(normalizedActivity);
    setDisable(!isEditable);
  }

  const fetchCompany = async () => {
    setLoading(prev => ({ ...prev, fetch: true }));
    try {
      const id = reservationForm.getFieldValue(formName);
      if (id) {
        const [response] = await getCompany({ id });

        form.setFieldsValue({
          'id': response.id,
          'active': response.active,
          'documentType': response.document_type,
          'documentNumber': response.document_type === 'rut' ? format(response.document_number) : response.document_number,
          'businessName': response.business_name,
          'fantasyName': response.fantasy_name,
          'activity': response.activity,
          'email': response.email,
          'web': response.web,
          'address': response.address.name,
          'number': response.address.number,
          'communeId': response.commune.id,
          'phone': response.phone.number,
          'codArea': response.phone.code,
          'cellPhone': response.cell_phone,
        })
        disableCompanyDataValidation(response)
        setDocumentType(response.document_type);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al obtener los datos de la empresa',
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  }

  useEffect(() => {
    fetchCommunes();
  }, [])

  useEffect(() => {
    if (companyModal && form) {
      form.resetFields();
      if (companyModal.type === 'edit') {
        fetchCompany();
      } else {
        form.setFieldsValue({ 'documentType': countrySettings.documentType[countryCode] })
        setDocumentType(countrySettings.documentType[countryCode])
      }
      disableCompanyDataValidation({
        documentType: countrySettings.documentType[countryCode],
        activity: reservationForm?.getFieldValue('activity'),
      });
    }
  }, [companyModal.visible]);

  const closeModal = () => {
    form.resetFields();
    setCompanyModal({ ...companyModal, visible: false });
  };

  const submit = () => {
    form
      .validateFields()
      .then(() => {
        setLoading(prev => ({ ...prev, submit: true }));
        const values = form.getFieldsValue();
        let responsePromise;
        const params = {
          active: values.active,
          activity: values.activity,
          address: values.address,
          number: values.number,
          web: values.web,
          business_name: values.businessName,
          fantasy_name: values.fantasyName,
          commune_id: values.communeId,
          cod_area: values.codArea,
          phone: values.phone.includes('+') ? values.phone : `+${values.phone}`,
          cell_phone: values.cellPhone.includes('+') ? values.cellPhone : `+${values.cellPhone}`,
          email: values.email,
          document_number: values.documentType === 'rut' ? format(values.documentNumber, { dots: false }) : values.documentNumber,
          document_type: values.documentType,
        };

        if (values.id) {
          responsePromise = updateCompany(values.id, params);
        } else {
          responsePromise = createCompany(params);
        }
        responsePromise.then(response => {
          response = Array.isArray(response) ? response[0] : response;
          if (response.status === "error") {
            message.error({
              content: Object.values(response.data)[0],
              style: {
                marginTop: '40vh',
              },
            });
          } else {
            addCompany({
              is_provider: response.is_provider,
              business_name: response.business_name,
              id: response.id
            });

            reservationForm.setFieldsValue({
              [formName]: response.id
            });

            message.success({
              content: `Compañía ${companyModal.type === 'edit' ? 'actualizada' : 'creada'} exitosamente`,
              style: {
                marginTop: '40vh',
              },
            });
          }
          form.resetFields();
          setCompanyModal({ ...companyModal, visible: false });
        }).catch((error) => {
          let errorMessage;
          console.error(error)
          if (handlers.isJSON(error)) {
            errorMessage = error?.message?.message || error.message;
          } else {
            errorMessage = error;
          }
          message.error({
            content: errorMessage,
            style: {
              marginTop: '30vh',
            },
          });

        }).finally(() => {
          setLoading(prev => ({ ...prev, submit: false }));
        });
      });
  };

  const handleDocumentNumber = async event => {
    let inputValue = documentType === 'rut' ? format(event.target.value, { dots: false }) : event.target.value;

    if (!inputValue || inputValue.trim() === '' || inputValue === '-') {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      if (
        (documentType === 'ruc' && inputValue.length === 11)
        || (documentType === 'rut' && inputValue.length > 6)
      ) {

        const response = await getTributaryCompany({
          document_number: inputValue,
          document_type: documentType,
        });
        if (response) {
          form.setFieldsValue({
            businessName: response.business_name,
            activity: response.activity,
            address: response.address,
            number: response.number,
          });
          disableCompanyDataValidation({
            documentType: documentType,
            activity: response.activity,
          });
        }
      } else if ((documentType === 'dni' && inputValue.length >= 8)
        || (documentType === 'passport' && inputValue.length >= 6 && inputValue.length <= 9)
        || (documentType === 'ce' && inputValue.length >= 7 && inputValue.length <= 9)) {
        form.setFieldsValue({
          businessName: '',
          activity: countrySettings.defaultCompanyActivity[countryCode],
          address: '',
          number: '',
        });
        disableCompanyDataValidation({
          documentType: documentType,
          activity: countrySettings.defaultCompanyActivity[countryCode],
        });
      } else {
        throw new Error(`${documentType} incorrecto`);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: 'No se pueden obtener los datos de la empresa',
        style: {
          marginTop: '30vh',
        },
      });
      form.setFieldsValue({
        businessName: '',
        activity: countrySettings.defaultCompanyActivity[countryCode],
        address: '',
        number: '',
      });
      disableCompanyDataValidation({
        documentType: documentType,
        activity: countrySettings.defaultCompanyActivity[countryCode],
      });
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }

  };

  return (
    <Modal
      className="creation-modal company"
      open={companyModal.visible}
      onCancel={closeModal}
      footer={false}
    >
      <Spin spinning={loading.fetch}>
        <Form form={form} layout="vertical">
          <Row>
            <Col lg={24} xs={24}>
              <Row justify='center'>
                <Col>
                  <h1>
                    {companyModal.type === 'create' ? createTitle : editTitle}
                  </h1>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col lg={18} xs={24}>
                  <Form.Item name="id" hidden>
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label={t('reservations.companyModal.activeCompanyLabel')}
                    name="active"
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Radio.Group className="cmt-radio">
                      <Radio value={true}>Si</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Row className='documentType-wrapper'>
                    <Col lg={24} xs={24}>
                      <p>Tipo y número de documento</p>
                    </Col>
                    <Col lg={24} xs={24} className='creation-modal company document-select-wrapper'>
                      <DocumentCard
                        countryCode={countryCode}
                        setDocumentType={setDocumentType}
                        form={form}
                        handleOnDocumentNumberBlur={handleDocumentNumber}
                        type='all'
                        disabled={companyModal.type === 'edit'}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="businessName"
                    label={t('reservations.companyModal.companyLabel')}
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Input
                      className="cmt-input"
                      disabled={disable}
                      id="businessNameTest"
                      placeholder='Ingrese nombre compañía'
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="activity"
                    label={t('reservations.companyModal.activityLabel')}
                  >
                    <Input
                      className="cmt-input"
                      disabled={disable}
                      placeholder={t('reservations.companyModal.activityPlaceholder')}
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="fantasyName"
                    label={t('reservations.companyModal.fantasyNameLabel')}
                  >
                    <Input
                      className="cmt-input"
                      id="fantasyNameTest"
                      placeholder='Ingrese nombre de fantasía'
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="email"
                    label={t('reservations.companyModal.emailLabel')}
                    rules={[
                      { required: true, message: 'Este campo es requerido' },
                      { type: 'email', message: 'El email es inválido' }
                    ]}
                  >
                    <Input
                      className="cmt-input"
                      type="email"
                      id="emailTest"
                      placeholder='Ingrese nombre email'
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="web"
                    label={t('reservations.companyModal.webLabel')}
                  >
                    <Input
                      className="cmt-input"
                      placeholder='Ingrese página web'
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="address"
                    label={t('reservations.companyModal.addressLabel')}
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Input
                      className="cmt-input"
                      placeholder='Ingrese dirección'
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="number"
                    label={t('reservations.companyModal.streetNumberLabel')}
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Input
                      className="cmt-input"
                      placeholder='Ingrese número de calle'
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    label={t('reservations.companyModal.communeLabel')}
                    name="communeId"
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                    className='commune-select-wrapper'
                  >
                    <Select
                      placeholder={t('reservations.companyModal.communeLabel')}
                      className="cmt-select"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        return (
                          option.key.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                          option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        );

                      }}
                    >
                      {
                        communes && communes.length && communes.map((commune) => {
                          return (
                            <Option
                              key={commune.id}
                              title={commune.name}
                              value={commune.id}
                              code={commune.cod_area}
                            >
                              {commune.name}
                            </Option>
                          )
                        })
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <PhoneInput.ReactPhone
                    countryCode={countryCode}
                    form={form}
                    labelText={t('reservations.companyModal.phoneLabel')}
                  />
                </Col>
                <Col lg={8} xs={24}>
                  <PhoneInput.ReactPhone
                    countryCode={countryCode}
                    form={form}
                    labelText={t('reservations.companyModal.cellPhoneLabel')}
                    formName='cellPhone'
                  />
                </Col>
              </Row>
              <Row justify='center' className="mt-20" gutter={[15, 15]}>
                <Col lg={8} xs={24}>
                  <Button
                    disabled={loading.submit}
                    className='cmt-button primary outline'
                    type='primary'
                    onClick={closeModal}
                  >
                    Cancelar
                  </Button>
                </Col>
                <Col lg={8} xs={24}>
                  <Button
                    loading={loading.submit}
                    disabled={loading.submit}
                    className='cmt-button primary'
                    type='primary'
                    onClick={submit}
                  >
                    {companyModal.type === 'create' ? createTitle : editTitle}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal >
  )
}

export default CreateCompanyModal;
